<template>
  <div class="image-gallery">
    <slot></slot>

    <portal v-if="showingModal || isAnimating" to="root">
      <div
        class="gallery-wrapper"
        :class="{isAnimating}"

        id="gallery-wrapper"
        ref="gallery"
      >
        <div class="gallery-header d-flex justify-content-between" ref="gallery-header">
          <span>
            {{ selected + 1 }} / {{ images.length }}
          </span>
          <span>
            <svg class="close" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 20 20" fill="currentColor" @click="selected = null">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </span>
        </div>

        <div class="grid-wrapper" ref="grid-wrapper">
          <svg v-show="selected > 0" class="arrow-left" xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" viewBox="0 0 24 24" stroke="currentColor" @click="goTo(selected - 1)">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>

          <div class="images-grid" ref="scroller">
            <div v-for="(src, index) in images" :key="src + index"
              class="image-item"
              @click.self="selected = null"
            >
              <img :src="src" class="img">
            </div>
          </div>

          <svg v-show="(selected + 1) < images.length" class="arrow-right" xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 20 20" fill="currentColor" @click="goTo(selected + 1)">
            <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </div>

        <div class="gallery-footer d-flex" ref="gallery-footer">
          <img v-for="(src, index) in images" :key="src + index"
            class="miniatur-img"
            :class="{active: index === selected}"

            :src="src"
            width="80"
            height="80"

            @click="goTo(index)"
          >
        </div>
      </div>
    </portal>
  </div>
</template>

<script>

export default {
  props: {
    value: Number,
    imgTagSelector: String,
    images: Array,
  },
  data() {
    return {
      selected: null,
      width: null,

      isAnimating: false,
    }
  },
  mounted() {
    window.addEventListener("resize", this.scrollToCurrentImage, {passive: true})
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.scrollToCurrentImage)
  },
  methods: {
    scrollToCurrentImage() {
      this.goTo(this.selected)
    },
    goTo(index) {
      const gallery = document.getElementById("gallery-wrapper")
      if (!gallery) return;
      const imageItem = gallery.querySelector(".image-item")
      if (!imageItem || !this.$refs["scroller"]) return;
      this.width = imageItem.getBoundingClientRect().width

      if (index < 0) index = 0
      else if (index > (this.images.length - 1)) index = this.images.length - 1

      this.selected = index
      this.$refs["scroller"].style.transform = `translateX(-${(this.width * index)}px)`
    },


    imgFlipAnimation() {
      return new Promise(solve => {
        let target
        if (this.imgTagSelector) {
          target = this.$el.querySelectorAll(this.imgTagSelector)[this.selected]
        }
        if (!target) return solve()

        const mainImg = document.getElementById("gallery-wrapper").querySelectorAll(".image-item")[this.selected].querySelector("img")
        const s = mainImg.style

        s.opacity = 0
        s.transitionDuration = '0'


        requestAnimationFrame(() => {
          const rect = target.getBoundingClientRect()
          const newRect = mainImg.getBoundingClientRect()
          const headerHeight = this.$refs["gallery-header"].clientHeight

          const width = this.width  * this.selected

          s.width = rect.width + "px"
          s.height = rect.height + "px"
          s.left = (width + rect.left) + "px"
          s.top = rect.top + "px"
          s.transform = `translateY(-${headerHeight}px)`
          s.position = "absolute"
          s.opacity = 1

          requestAnimationFrame(() => {
            s.width = newRect.width + "px"
            s.height = newRect.height + "px"
            s.left = (width + newRect.left) + "px"
            s.top = newRect.top + "px"
            s.transitionDuration = ".15s"

            const listener = () => {
              s.width = "auto"
              s.height = "auto"
              s.left = "unset"
              s.top = "unset"
              s.transitionDuration = "unset"
              s.transform = "translate(0px, 0px)"
              s.position = "relative"

              mainImg.removeEventListener("transitionend", listener)
              solve()
            }
            mainImg.addEventListener("transitionend", listener)
          })
        })
      })
    },
    animateBackgroundOpen() {
      return new Promise(solve => {
        const gallery = this.$refs["gallery"]
        if (!gallery) return solve()
        const s = gallery.style

        s.backgroundColor = "rgba(0,0,0,0)"

        requestAnimationFrame(() => {
          s.backgroundColor = "rgba(0,0,0,.9)"
          s.transitionDuration = ".2s"

          const listener = () => {
            gallery.removeEventListener("transitionend", listener)
            solve()
          }
          gallery.addEventListener("transitionend", listener)
        })
      })
    },
    async animateOpen() {
      this.isAnimating = true

      await Promise.all([
        this.imgFlipAnimation(),
        this.animateBackgroundOpen(),
      ])

      this.isAnimating = false
    },
    async animateClose() {
      this.isAnimating = true

      const gallery = this.$refs["gallery"]
      const s = gallery.style

      s.opacity = 1
      s.transitionDuration = "0"

      requestAnimationFrame(() => {
        s.opacity = 0
        s.transitionDuration = ".2s"

        const listener = () => {
          gallery.removeEventListener("transitionend", listener)
          this.isAnimating = false
        }
        gallery.addEventListener("transitionend", listener)
      })
    },
  },
  computed: {
    showingModal() {
      return !!(this.selected || this.selected === 0)
    },
  },
  watch: {
    showingModal(val) {
      if (val) {
        this.closing = false

        requestAnimationFrame(() => {
          this.animateOpen()
          this.goTo(this.value)
        })
      } else {
        this.animateClose()
      }

      requestAnimationFrame(() => {
        const gallery = this.$refs["gallery-footer"]
        const scrollHeight = gallery.offsetHeight - gallery.clientHeight
        gallery.style.transform = `translateY(${scrollHeight}px)`
      })
    },
    selected(val) {
      this.$emit("input", val)

      ;(val || val === 0) && requestAnimationFrame(() => {
        const scroller = this.$refs["gallery-footer"]
        const target = scroller.children[val]

        scroller.scrollLeft = (target.offsetLeft - (window.innerWidth / 2)) + (target.clientWidth / 2)
      })
    },
    value(val) {
      this.selected = val
      if (!this.selected && this.selected !== 0) {
        this.selected = null
      }
    },
  },
}

</script>

<style scoped>

.images-grid, .gallery-wrapper {
  width: 100%;
  height: 100%;
}

.arrow-right, .arrow-left {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 30px;
  color: #999;
  transition-duration: .2s;
  cursor: pointer;
  z-index: 5;
  padding: 14px;
  background-color: rgb(0,0,0,.4);
}

.arrow-left {
  left: 30px;
}

@media (max-width: 696px) {
  .arrow-left, .arrow-right {
    color: white;
    right: 12px;
  }

  .arrow-left {
    left: 12px;
  }
}

.arrow-right:hover, .arrow-left:hover {
  color: white;
}

.close {
  color: #999;
  transition-duration: .2s;
  cursor: pointer;
}

.close:hover {
  color: white;
}

.gallery-header, .gallery-footer {
  padding: 12px;
  font-size: 1em;
  color: white;
}

.gallery-footer {
  margin-top: 26px;
  height: 160px;
  scroll-behavior: smooth;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.grid-wrapper {
  position: relative;
  width: 100%;
  flex-basis: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.images-grid {
  position: absolute;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  height: 100%;
  width: 100%;
  transform: translateX(0px);
  transition: .2s ease-in-out;
}

.isAnimating .images-grid {
  transition: none !important;
}

.image-item {
  width: 100vw;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}

.img {
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 1200px;
  display: block;
  object-fit: cover;
}

@media (max-width: 696px) {
  .img {
    object-fit: scale-down;
  }
}

.isAnimating .img {
  object-fit: cover;
}



.miniatur-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 1px !important;
  cursor: pointer;
  border: 2px solid white;
  transition-duration: .2s;
}

.miniatur-img:hover {
  border: 2px solid #0081c7;
}

.miniatur-img.active {
  border: 2px solid #a90707;
}

.miniatur-img + .miniatur-img {
  margin-left: 4px;
}

.gallery-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  flex-direction: column;
  display: flex;
  background-color: rgba(0,0,0,.9);
  z-index: 9999;
}

.gallery-wrapper > div {
  position: relative;
}

#categories-collection .image-gallery {
  /*max-height: 355px !important;*/
  overflow-y: auto;
}

.image-gallery::-webkit-scrollbar {
  width: 6px;
}

/*
.image-gallery::-webkit-scrollbar-track {
  border-radius: 10px;
}
*/

/*.image-gallery::-webkit-scrollbar-thumb {
  background: #D1D3D4;
  border-radius: 10px;
}*/

/*
.image-gallery::-webkit-scrollbar-thumb:hover {
  background: #D1D3D4;
}
*/

</style>
